/**
 * AdvertiserSelect Component
 * Searchable single-select dropdown for choosing an advertiser
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, ChevronDown, Check, Building2, Loader2 } from 'lucide-react'
import { advertiserApi, Advertiser } from '../../lib/api/advertisers'

interface AdvertiserSelectProps {
  value: Advertiser | null
  onChange: (advertiser: Advertiser | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function AdvertiserSelect({
  value,
  onChange,
  placeholder = 'Select advertiser...',
  disabled = false,
  className = ''
}: AdvertiserSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Load advertisers
  const loadAdvertisers = useCallback(async (page: number, query: string, append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
    }

    try {
      const { advertisers: data, meta } = await advertiserApi.getAdvertisers({
        page,
        per_page: 20,
        q: query || undefined
      })

      if (append) {
        setAdvertisers(prev => [...prev, ...data])
      } else {
        setAdvertisers(data)
      }
      
      setCurrentPage(meta.current_page)
      setHasMore(meta.current_page < meta.total_pages)
    } catch (error) {
      console.error('Failed to load advertisers:', error)
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [])

  // Ensure selected advertiser is in the list (prepend if not found)
  const displayAdvertisers = (() => {
    if (!value || !advertisers.length) return advertisers
    
    // Check if the selected advertiser is already in the list
    const isInList = advertisers.some(a => a.id === value.id)
    
    if (isInList) {
      return advertisers
    }
    
    // If searching and selected isn't in results, don't prepend (user is searching for something else)
    if (searchQuery) {
      return advertisers
    }
    
    // Prepend selected advertiser to the list
    return [value, ...advertisers]
  })()

  // Load initial advertisers when dropdown opens
  useEffect(() => {
    if (isOpen && advertisers.length === 0) {
      loadAdvertisers(1, '')
    }
  }, [isOpen, advertisers.length, loadAdvertisers])

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    
    searchTimerRef.current = setTimeout(() => {
      loadAdvertisers(1, value)
    }, 300)
  }

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const { scrollTop, scrollHeight, clientHeight } = target
    
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isLoadingMore) {
      loadAdvertisers(currentPage + 1, searchQuery, true)
    }
  }

  // Handle selection
  const handleSelect = (advertiser: Advertiser) => {
    onChange(advertiser)
    setIsOpen(false)
    setSearchQuery('')
  }

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Selected Value / Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left border rounded-lg transition-colors ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60'
            : 'bg-white dark:bg-gray-900 hover:border-gray-400 dark:hover:border-gray-600 cursor-pointer'
        } ${
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500/20'
            : 'border-gray-300 dark:border-gray-700'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {value ? (
            <span className="text-sm text-gray-900 dark:text-white truncate">
              {value.name}
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {placeholder}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search advertisers..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Advertisers List */}
          <div 
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-72 overflow-y-auto scrollbar-thin"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : displayAdvertisers.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchQuery ? 'No advertisers found' : 'No advertisers available'}
                </p>
              </div>
            ) : (
              <>
                {displayAdvertisers.map((advertiser) => {
                  const isSelected = value?.id === advertiser.id
                  return (
                    <button
                      key={advertiser.id}
                      type="button"
                      onClick={() => handleSelect(advertiser)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                        isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${isSelected ? 'text-primary-700 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}`}>
                          {advertiser.name}
                        </p>
                        {advertiser.company_name && advertiser.company_name !== advertiser.name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {advertiser.company_name}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                      )}
                    </button>
                  )
                })}
                
                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

